import { useState, useEffect, useCallback } from 'react'
import { useDrawer, DrawerFooter } from '@/components/ui/drawer'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { apiService } from '@/services/api'
import { toast } from 'sonner'
import { Coins, DollarSign } from 'lucide-react'

interface AddCreditsDrawerProps {
  tenantId: string
  tenantName: string
  onSuccess?: () => void
}

// Store form state globally for access by footer
const formStates = new Map<string, {
  handleAdd: () => Promise<void>
  saving: boolean
  isValid: boolean
}>()

export function AddCreditsDrawer({ tenantId, tenantName, onSuccess }: AddCreditsDrawerProps) {
  const { closeDrawer, updateDrawer } = useDrawer()
  const [saving, setSaving] = useState(false)
  const [amount, setAmount] = useState<string>('')
  const [description, setDescription] = useState<string>('')

  const drawerId = `add-credits-${tenantId}`

  const isValid = useCallback(() => {
    const amountNum = parseFloat(amount)
    return amount.trim() !== '' && !isNaN(amountNum) && amountNum > 0
  }, [amount])

  const handleAdd = useCallback(async () => {
    if (!isValid()) {
      toast.error('Please enter a valid amount greater than 0')
      return
    }

    setSaving(true)

    try {
      const amountNum = parseFloat(amount)

      await apiService.credits.add({
        tenant_id: tenantId,
        amount: amountNum,
        description: description || `Manual credit addition: ${amountNum} credits`,
      })

      toast.success(`Successfully added ${amountNum} credit${amountNum === 1 ? '' : 's'} to ${tenantName}`)

      // Call success callback
      onSuccess?.()

      // Show success animation and close
      updateDrawer(drawerId, { loadingState: 'success' })
      setTimeout(() => {
        closeDrawer(drawerId)
      }, 1000)
    } catch (error) {
      console.error('Failed to add credits:', error)
      toast.error('Failed to add credits')
    } finally {
      setSaving(false)
    }
  }, [amount, description, tenantId, tenantName, onSuccess, updateDrawer, drawerId, closeDrawer, isValid])

  // Store form state for footer access
  useEffect(() => {
    formStates.set(drawerId, {
      handleAdd,
      saving,
      isValid: isValid(),
    })

    return () => {
      formStates.delete(drawerId)
    }
  }, [drawerId, handleAdd, saving, isValid])

  return (
    <div className="space-y-6">
      {/* Tenant Info */}
      <div className="bg-muted/50 rounded-lg p-4">
        <p className="text-sm text-muted-foreground">Adding credits to:</p>
        <p className="font-semibold text-lg">{tenantName}</p>
      </div>

      {/* Amount Input */}
      <div>
        <div className="flex items-center gap-2 mb-4">
          <Coins className="h-5 w-5 text-primary" />
          <h3 className="text-lg font-semibold">Credit Amount</h3>
        </div>

        <div className="space-y-4">
          <div>
            <Label htmlFor="amount">Amount *</Label>
            <div className="relative">
              <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                id="amount"
                type="number"
                min="0"
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="Enter credit amount"
                className="mt-1 pl-10"
              />
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Enter the number of credits to add to this tenant's account
            </p>
          </div>

          <div>
            <Label htmlFor="description">Description (Optional)</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Reason for credit addition (e.g., 'Promotional bonus', 'Refund for issue #123')"
              className="mt-1 min-h-[100px]"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Optional note describing why these credits are being added
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

// Footer component that accesses form state
export function AddCreditsFooter({ drawerId }: { drawerId: string }) {
  const { closeDrawer } = useDrawer()
  const [formState, setFormState] = useState(formStates.get(drawerId))

  useEffect(() => {
    const interval = setInterval(() => {
      setFormState(formStates.get(drawerId))
    }, 100)

    return () => clearInterval(interval)
  }, [drawerId])

  return (
    <DrawerFooter>
      <Button
        variant="outline"
        onClick={() => closeDrawer(drawerId)}
        disabled={formState?.saving}
      >
        Cancel
      </Button>
      <Button
        onClick={() => formState?.handleAdd()}
        disabled={formState?.saving || !formState?.isValid}
      >
        {formState?.saving ? 'Adding Credits...' : 'Add Credits'}
      </Button>
    </DrawerFooter>
  )
}
